import { useCallback, useEffect, useRef } from 'react';

const normalizeElements = (elements) => {
  if (!elements) return [];

  return (Array.isArray(elements) ? elements : [elements]).filter(Boolean);
};

const restoreStyleAttribute = (element, styleAttribute) => {
  if (styleAttribute === null) {
    element.removeAttribute('style');
    return;
  }

  element.setAttribute('style', styleAttribute);
};

const useTemporaryInlineStyles = () => {
  const originalStylesRef = useRef(new Map());

  const setTemporaryStyles = useCallback(({ elements, styles }) => {
    const properties = Object.entries(styles || {});

    if (!properties.length) return;

    normalizeElements(elements).forEach((element) => {
      if (!originalStylesRef.current.has(element)) {
        originalStylesRef.current.set(element, element.getAttribute('style'));
      }

      properties.forEach(([property, value]) => {
        element.style.setProperty(property, value);
      });
    });
  }, []);

  const restoreTemporaryStyles = useCallback(({ elements }) => {
    normalizeElements(elements).forEach((element) => {
      if (!originalStylesRef.current.has(element)) return;

      restoreStyleAttribute(element, originalStylesRef.current.get(element));
      originalStylesRef.current.delete(element);
    });
  }, []);

  useEffect(() => {
    const originalStyles = originalStylesRef.current;

    return () => {
      originalStyles.forEach((styleAttribute, element) => {
        restoreStyleAttribute(element, styleAttribute);
      });
      originalStyles.clear();
    };
  }, []);

  return {
    setTemporaryStyles,
    restoreTemporaryStyles,
  };
};

export default useTemporaryInlineStyles;
